import { ENDPOINTS } from 'config/endpoints';
import { useQueryClient } from '@tanstack/react-query';
import CronoError from 'crono-fe-common/types/error';
import client from 'utils/clients/client';
import { Request } from 'crono-fe-common/types/request';
import { Response } from 'crono-fe-common/types/response';
import { AddProspectToStrategyInputs } from 'crono-fe-common/types/DTO/addProspectToStrategyInputs';
import { useLimitedMutation } from '../useLimitedMutation';
import {
  useBackgroundInvalidateAutomatorDelay,
  useEnqueueAsyncRewriteJob,
} from 'crono-fe-common/hooks/crono-extension/gateway';
import { WithDataForPossibleRewrite } from 'crono-fe-common/types/withDataForPossibleRewrite';
import {
  enqueueRewriteSequenceInstanceIfNeeded,
  isProspectOwned,
} from 'crono-fe-common/utils';
import {
  CronoExtensionStatus,
  useGetExtensionStatus,
} from '../crono-extension/useGetExtensionStatus';
import { SequenceInstance } from 'crono-fe-common/types/sequenceInstance';
import { invalidateTimelineQueries } from 'hooks/services/timeline/useSearchTimeline';
import { useRoleCapabilities } from 'hooks/useRoleCapabilities';

export default function useAddProspectToStrategy({
  onSuccess,
}: {
  onSuccess?: (
    result: Response<SequenceInstance | null>,
    variables: WithDataForPossibleRewrite<AddProspectToStrategyInputs>,
  ) => void;
} = {}) {
  const queryClient = useQueryClient();
  const { data: extensionStatus } = useGetExtensionStatus();
  const { isManager } = useRoleCapabilities();

  const addProspectToStrategy = ({
    data: { assignToOwner, ...data },
    recipient,
  }: WithDataForPossibleRewrite<AddProspectToStrategyInputs>): Promise<
    Response<null>
  > => {
    //isManager is never overridable by the caller: a non-manager sending the
    //flag is rejected with 403, so it gates the explicit value too
    const enrolOnBehalfOfOwner =
      isManager && (assignToOwner ?? !isProspectOwned(recipient));

    const request: Request = {
      url: `${ENDPOINTS.strategy.prospect}`,
      config: {
        method: 'post',
        data: {
          ...data,
          //The flag is omitted rather than sent as false: non-managers sending it at all are rejected with 403
          ...(enrolOnBehalfOfOwner && { assignToOwner: true }),
          linkedinExtensionAvailable:
            extensionStatus?.status ===
            CronoExtensionStatus.INSTALLED_WITH_LINKEDIN_COOKIE,
        },
      },
    };

    return client(request);
  };
  const { call: invalidateAutomatorDelay } =
    useBackgroundInvalidateAutomatorDelay();
  const { call: enqueueRewriteJob } = useEnqueueAsyncRewriteJob();

  return useLimitedMutation<
    Response<SequenceInstance | null>,
    CronoError,
    WithDataForPossibleRewrite<AddProspectToStrategyInputs>
  >(addProspectToStrategy, {
    onSettled: (result, error, variables) => {
      invalidateAutomatorDelay({});
      queryClient.invalidateQueries([ENDPOINTS.strategy.resumableCount]);
      queryClient.invalidateQueries([ENDPOINTS.account.search.main]);
      queryClient.invalidateQueries([ENDPOINTS.prospect.main]);
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.toString().startsWith(ENDPOINTS.prospect.search.main),
      });
      queryClient.invalidateQueries([ENDPOINTS.prospect.search.strategy]);

      queryClient.invalidateQueries([ENDPOINTS.strategy.search]);
      queryClient.invalidateQueries([
        ENDPOINTS.strategy.details,
        variables.data.strategyId,
      ]);
      queryClient.invalidateQueries([
        ENDPOINTS.strategy.main,
        variables.data.strategyId,
      ]);
      queryClient.invalidateQueries([ENDPOINTS.task.linkedin.quantity]);
      queryClient.invalidateQueries([ENDPOINTS.task.linkedin.automatic.check]);
      queryClient.invalidateQueries([ENDPOINTS.task.search]);
      invalidateTimelineQueries(queryClient);
      queryClient.invalidateQueries([ENDPOINTS.task.main]);
      queryClient.invalidateQueries([ENDPOINTS.task.user.analytics]);
      queryClient.invalidateQueries([ENDPOINTS.user.onboarding.features]);
      queryClient.invalidateQueries([
        ENDPOINTS.assistant.rewriteSequenceInstanceInBulkProgress,
      ]);
      queryClient.invalidateQueries([ENDPOINTS.signal.search]);
      queryClient.invalidateQueries({
        queryKey: [ENDPOINTS.strategy.prospect],
      });
    },
    onSuccess: async (result, variables) => {
      onSuccess?.(result, variables);
      if (!variables.data.resume) {
        const createdSequenceInstance = result.data.data;
        variables.recipient.sequenceInstance = createdSequenceInstance;
        variables.recipient.sequenceInstanceId = createdSequenceInstance?.id;
        //The rewrite runs in this user's extension and personalises the owner's
        //tasks, so a contact enrolled on behalf of someone else is left out
        if (isProspectOwned(variables.recipient)) {
          await enqueueRewriteSequenceInstanceIfNeeded(
            variables,
            enqueueRewriteJob,
          );
        }
      }
    },
  });
}
